import { ApiError, apiClient } from '@/utils/apiClient';
import { getAppUrl } from '@/config/env';
import { isEmbed } from '@/config/embed';
import type { SelectionFailure } from '@/utils/landingAddressSubmit';

/**
 * Shared client for the Express ``/places/*`` proxy (Google Places API New).
 *
 * Both the interactive ``AutoCompleteAddressInput`` and the Stanford
 * address-from-URL pre-fill resolve raw text into a structured address the
 * same way, so the autocomplete + place-details calls and the US-territory
 * guard live here as a single source of truth.
 */

export interface PlaceAddressDetails {
  formatted_address: string;
  place_id: string;
  // Google Places address-component array; kept loose since the same blob is
  // forwarded to the backend on post-sign-in sync where it's already understood.
  address_components: any[];
  geometry: {
    location: {
      lat: number;
      lng: number;
    };
  };
}

export interface PlaceSuggestion {
  place_id: string;
  description: string;
  structured_formatting?: {
    main_text?: string;
    secondary_text?: string;
  };
}

/**
 * A failed proxy call. ``status`` is the HTTP status, or null when the request
 * never got a response. An address-less details body is reported with its
 * (usually 200) status, since the proxy relays Google errors that way.
 */
export interface PlacesLookupError {
  stage: 'autocomplete' | 'details';
  status: number | null;
}

export type OnPlacesLookupError = (error: PlacesLookupError) => void;

/** A tapped suggestion's lookup: the address, or why it can't be used. */
export type SelectionResult =
  | { details: PlaceAddressDetails; failure?: undefined }
  | { details: null; failure: SelectionFailure };

const errorStatus = (e: unknown): number | null => (e instanceof ApiError ? e.status : null);

// US + territories whose residents have US ballots. Mirrors the
// `includedRegionCodes` allow-list on the Express /places proxy.
export const ALLOWED_COUNTRY_CODES = new Set(['US', 'PR', 'GU', 'AS', 'VI', 'MP']);

// Base URL for the `/places/*` proxy. The proxy is Express middleware in the
// first-party server (`server.js`), so it only exists at the first-party
// origin. First-party pages call it same-origin (''). Embedded-widget pages
// are served from a static host with no server, so they target the first-party
// origin (`VITE_APP_URL`) where the proxy actually runs.
const placesBaseUrl = (): string => (isEmbed() ? (getAppUrl() ?? '') : '');

/**
 * Fetch autocomplete predictions for the given input text. Returns an empty
 * array on any error so callers can treat "no network / no match" uniformly.
 */
export const fetchPlaceAutocomplete = async (
  inputText: string,
  onError?: OnPlacesLookupError
): Promise<PlaceSuggestion[]> => {
  // Kept outside the try so a body that fails to parse still reports its status.
  let status: number | null = null;
  try {
    const res = await apiClient('/places/autocomplete', {
      method: 'POST',
      baseUrl: placesBaseUrl(),
      body: JSON.stringify({ inputText }),
    });
    status = res.status;

    const data: any = await res.json();
    const preds: any[] = data?.suggestions || [];
    return preds
      .map((s: any) => s?.placePrediction)
      .filter(Boolean)
      .map((p: any) => ({
        place_id: p.placeId,
        description: p.text?.text || '',
        structured_formatting: {
          main_text: p.structuredFormat?.mainText?.text,
          secondary_text: p.structuredFormat?.secondaryText?.text,
        },
      }));
  } catch (e) {
    console.error('Autocomplete REST error:', e);
    onError?.({ stage: 'autocomplete', status: errorStatus(e) ?? status });
    return [];
  }
};

/**
 * Resolve a place_id into full address details. Returns ``null`` on error.
 */
export const fetchPlaceDetails = async (
  placeId: string,
  onError?: OnPlacesLookupError
): Promise<PlaceAddressDetails | null> => {
  let status: number | null = null;
  try {
    const res = await apiClient(`/places/${encodeURIComponent(placeId)}`, {
      baseUrl: placesBaseUrl(),
    });
    status = res.status;

    const place: any = await res.json();
    // The proxy relays a Google error body with a 200, so an address-less
    // payload is a failed lookup, not an address.
    if (!place?.formattedAddress) {
      onError?.({ stage: 'details', status: res.status });
      return null;
    }
    const details: PlaceAddressDetails = {
      formatted_address: place.formattedAddress,
      place_id: place.id || placeId,
      address_components: place.addressComponents || [],
      geometry: {
        location: {
          lat: place?.location?.latitude ?? 0,
          lng: place?.location?.longitude ?? 0,
        },
      },
    };
    return details;
  } catch (e) {
    console.error('Place details REST error:', e);
    onError?.({ stage: 'details', status: errorStatus(e) ?? status });
    return null;
  }
};

/**
 * True when the resolved address is in the United States or a US territory
 * with US ballots. Defensive backstop for anything the backend filter lets
 * through. Treats a missing country component as allowed (the proxy already
 * restricts the region), only rejecting an explicit non-US country.
 */
export const isAllowedCountry = (details: PlaceAddressDetails): boolean => {
  const country = details.address_components?.find((c: any) =>
    (c?.types || []).includes('country')
  );
  const countryCode = country?.shortText || country?.short_name;
  return !countryCode || ALLOWED_COUNTRY_CODES.has(countryCode);
};

/** Look up a tapped suggestion, saying why when it can't be used. Mirrors mobile. */
export const lookupSelection = async (
  placeId: string,
  onError?: OnPlacesLookupError
): Promise<SelectionResult> => {
  const details = await fetchPlaceDetails(placeId, onError);
  if (!details) return { details: null, failure: 'lookup_failed' };
  if (!isAllowedCountry(details)) return { details: null, failure: 'unsupported_address' };
  return { details };
};

/**
 * Resolve a free-text address string into validated address details, or
 * ``null`` if it can't be matched to an allowed US address.
 *
 * Used by the Stanford address-from-URL pre-fill: it takes the top
 * autocomplete prediction for the text and fetches its details, applying the
 * same US-territory guard as a manual selection. Returning ``null`` is the
 * "couldn't verify" signal callers use to keep the submit action disabled —
 * so a link carrying damaged characters never enables a ballot lookup.
 */
export const resolveAddressFromText = async (
  text: string,
  onError?: OnPlacesLookupError
): Promise<PlaceAddressDetails | null> => {
  const trimmed = text.trim();
  if (trimmed.length < 2) return null;

  const suggestions = await fetchPlaceAutocomplete(trimmed, onError);
  const top = suggestions[0];
  if (!top) return null;

  const details = await fetchPlaceDetails(top.place_id, onError);
  if (!details || !isAllowedCountry(details)) return null;

  return details;
};
